import { _ } from "libs";
import React, { FC, useEffect, useState } from "react";
import { Field, Form } from "react-final-form";
import CardSelect from "./forms/controls/cardSelect";
import { Card, PaymentMethod, PaymentOption, Promotion, Voucher } from "../types";
import { FormApi } from "final-form";
import CardForm from "./settings/cardForm";
import Currency from "./currency";
import cx from "classnames";
import NetworkSelect from "./forms/controls/networkSelect";

type ExtraData = {
	[key: string]: string | number | boolean;
};
type OnPayClicked = (
	card: Card,
	voucher?: string,
	promotionId?: string,
	paymentMethod?: string,
	network?: string,
	startTrial?: boolean,
	data?: ExtraData
) => Promise<any>;

type Props = {
	description: string;
	amount: number;
	currency: string;
	cards: Card[];
	defaultCard: Card;
	onPayClicked?: OnPayClicked;
	data?: ExtraData;
	buttonLabel?: string;
	context?: (voucherCode?: string, promotionId?: string, startTrial?: boolean) => Promise<void>;
	precalculatedContext?: Promise<any>;
	getPaymentCards: () => Promise<void>;
	selectCard: (cardId: string) => void;
	selectPaymentNetwork: (network: string) => void;
	startTrial?: boolean;
};

const PaymentWindow: FC<Props> = ({
	description,
	amount,
	currency,
	cards,
	defaultCard,
	onPayClicked,
	data = {},
	getPaymentCards,
	selectCard,
	selectPaymentNetwork,
	context,
	precalculatedContext,
	buttonLabel = _("payBtn"),
	startTrial,
}: Props) => {
	const [showAddCard, setShowAddCard] = useState(false);
	const [showAddVoucher, setShowAddVoucher] = useState(false);
	const [voucher, setVoucher] = useState(null as Voucher);
	const [promotion, setPromotion] = useState(null as Promotion);
	const [paymentOptions, setPaymentOptions] = useState([] as PaymentOption[]);
	const [contextLoaded, setContextLoaded] = useState(false);

	const gcWalletInOption = (paymentOptions || []).find((po) => po.paymentMethod === PaymentMethod.GCWallet);
	const gcWalletEnabled = gcWalletInOption?.available;
	const creditCardEnabled = (paymentOptions || []).find((po) => po.paymentMethod === PaymentMethod.CreditCard)
		?.available;

	let balance = "";
	if (gcWalletInOption) {
		balance = `${gcWalletInOption.note} ${gcWalletInOption.currency}`;
	}

	var originalAmount: number;
	originalAmount = null;

	useEffect(() => {
		if (context) {
			let contextResult;
			if (precalculatedContext) {
				contextResult = precalculatedContext;
			} else {
				contextResult = context(null, null, startTrial);
			}
			contextResult.then((c: any) => {
				setContextLoaded(false);
				const { voucher } = handleContextResponse(c);
				if (voucher?.discount && voucher?.valid) {
					setShowAddVoucher(true);
				}
				setContextLoaded(true);
			});
		} else {
			setContextLoaded(true);
		}
	}, []);

	const handleContextResponse = (context: any) => {
		let voucher, promotion, paymentOptions;

		if (context && context.vouchers) {
			let voucher, promotion;
			if (Array.isArray(context.vouchers) && context.vouchers.length >= 1) {
				voucher = context.vouchers[0];
				setVoucher(voucher);
			}

			if (Array.isArray(context.promotions) && context.promotions.length >= 1) {
				promotion = context.promotions[0];
				setPromotion(promotion);
			} else {
				setPromotion(null);
			}
		}

		if (context && context.paymentOptions) {
			paymentOptions = context.paymentOptions;
			setPaymentOptions(paymentOptions);
		}

		return {
			voucher,
			promotion,
			paymentOptions,
		};
	};

	const validateVoucher = async (value?) => {
		let voucherCode: string = value;
		if (value) {
			voucherCode = value.replace(/[^0-9A-Z]+/gi, "");
		}

		if (voucher && voucherCode == voucher?.voucherCode) {
			return;
		}

		if (!voucherCode && !voucher) {
			return;
		}

		if (voucherCode?.length > 0 && voucherCode?.length <= 3) {
			return;
		}

		return await reloadContext(voucherCode, null, startTrial);
	};

	const reloadContext = async (voucherCode?, promotionId?, startTrial?) => {
		if (context) {
			const c: any = await context(voucherCode, promotionId, startTrial);
			return handleContextResponse(c);
		}
	};

	const calculateDiscount = () => {
		if (voucher && voucher.valid) {
			return voucher.discount;
		}

		if (promotion) {
			return promotion.discount;
		}

		return null;
	};

	console.log(voucher);

	if (promotion || (voucher && voucher.valid === true)) {
		originalAmount = amount;
		amount = amount - calculateDiscount();
	}

	return (
		<>
			<div className="paymentSummary">
				{promotion && <div className="promotionTitle">{promotion.promotionTitle}</div>}
				<div className="price">
					<Currency amount={amount} currency={currency} />
				</div>
				{originalAmount !== null ? (
					<div className="originalPrice">
						<Currency amount={originalAmount} currency={currency} />
					</div>
				) : null}
				<div className="subject">{description}</div>
			</div>
			<div className="paymentForm">
				{showAddCard ? (
					<div className="addCardInlineForm">
						<div
							className="link blue"
							onClick={() => {
								setShowAddCard(false);
							}}
						>
							{_("cancelBtn")}
						</div>
						<CardForm
							onSubmit={async (paymentMethod) => {
								let cardId: string;
								if (typeof paymentMethod === "string") {
									cardId = paymentMethod;
								} else {
									cardId = paymentMethod.id;
								}
								await getPaymentCards();
								selectCard(cardId);
								setShowAddCard(false);
							}}
						/>
					</div>
				) : cards.length === 0 ? (
					<div className="button blue" onClick={() => setShowAddCard(true)}>
						{_("addCardBtn")}
					</div>
				) : (
					<Form
						onSubmit={async (
							{ card, voucher, useWallet, network }: any,
							formApi: FormApi<object>,
							callback
						) => {
							if (card && !network) {
								if (card.networks?.available?.length >= 1) {
									network = card.networks.preferred || card.networks.available[0];
								}
							}
							await onPayClicked(
								card,
								voucher,
								promotion?.promotionId,
								network,
								useWallet ? PaymentMethod.GCWallet : PaymentMethod.CreditCard,
								startTrial,
								data
							);
							callback();
						}}
						validate={(values: any) => {
							let ps = [];
							let t;
							if (t) {
								t.reset();
							}
							const validateVoucherPromise = new Promise((resolve, reject) => {
								t = setTimeout(() => {
									validateVoucher(values.voucher).then((res: any) => {
										if (res && res?.voucher) {
											if (res.voucher.valid === false) {
												resolve({
													key: "voucher",
													error: res.voucher.invalidReason,
												});
											}
										}
										resolve();
									});
								}, 500);
							});
							ps.push(validateVoucherPromise);
							return Promise.allSettled(ps).then((promises) => {
								let errors = {};
								promises.forEach((p: any) => {
									if (p.status == "fulfilled" && p.value && p.value?.key) {
										errors[p.value.key] = p.p.value.error;
									}
								});
								return errors;
							});
						}}
						initialValues={{ card: defaultCard }}
						render={({ handleSubmit, submitting, pristine, invalid, values, validating }) => (
							//{({console.log()})}
							<form onSubmit={handleSubmit} className="fullWidth">
								{creditCardEnabled && (
									<>
										<Field name="card">
											{({ input, meta, ...rest }) => (
												<div className="field">
													<label>{_("paymentForm.card.label")}</label>
													<CardSelect
														{...{ input, meta, ...rest }}
														cards={cards}
														prompt={_("paymentForm.card.prompt")}
													/>
													{meta.touched && meta.error && <span>{meta.error}</span>}
												</div>
											)}
										</Field>
										{values.card?.networks?.available?.length > 1 && (
											<Field name="network">
												{({ input, meta, ...rest }) => (
													<div className="field">
														<label>{_("bookingWizard.cardNetworks.label")}</label>
														<NetworkSelect
															{...{ input, meta, ...rest }}
															card={values.card}
														/>
														{meta.touched && meta.error && <span>{meta.error}</span>}
													</div>
												)}
											</Field>
										)}
									</>
								)}
								{!!gcWalletInOption && (
									<Field name="useWallet">
										{({ input, meta, ...rest }) => (
											<>
												<label className="mt-4">{_("paymentForm.gc_wallet.label")}</label>
												<div className="field checkbox">
													<label>
														<input {...input} type="checkbox" disabled={!gcWalletEnabled} />
														<span className={`${!gcWalletEnabled ? "disabled" : ""}`}>
															{balance}
														</span>
													</label>
													{meta.touched && meta.error && <span>{meta.error}</span>}
												</div>
											</>
										)}
									</Field>
								)}
								{showAddVoucher && (
									<Field name="voucher">
										{({ input, meta, ...rest }) => (
											<div className="field">
												<label>{_("paymentForm.voucher.label")}</label>
												<input {...input} type="text" disabled={!contextLoaded} />
												{voucher?.valid && (
													<div className="voucherValue">
														-
														<Currency
															amount={voucher.discount}
															currency={voucher.discountCurrency}
														/>
													</div>
												)}
												{meta.touched && meta.error && <span>{meta.error}</span>}
											</div>
										)}
									</Field>
								)}
								{contextLoaded &&
									((!showAddVoucher && !voucher) ||
										(!showAddVoucher && voucher && voucher.available)) && (
										<div className="enterVoucher">
											<span className="link blue" onClick={() => setShowAddVoucher(true)}>
												{_("paymentForm.voucher.enter")}
											</span>
										</div>
									)}
								<div className="buttons">
									<button
										type="submit"
										className={cx("button", "large", "fullWidth", { loading: submitting })}
										disabled={submitting || invalid || validating}
									>
										{buttonLabel}
									</button>
								</div>
							</form>
						)}
					/>
				)}
			</div>
		</>
	);
};

export default PaymentWindow;
